import { TITLE_MAX } from '@cult/domain';
import { z } from 'zod';

// ── Reusable field definitions ─────────────────────────────────────────────
export const idField = z.string();
export const createdAtField = z.string();
export const updatedAtField = z.string();
export const qField = z.string().trim().min(1).max(100).optional();

export const createdRangeFields = {
  createdFrom: z.string().datetime().optional(),
  createdTo: z.string().datetime().optional(),
};
export const titleField = z.string().trim().min(1).max(TITLE_MAX);

// ── English-only submission text ───────────────────────────────────────────
// Ideas, praises and feedback are English-only: printable ASCII plus tabs and line breaks.
const NON_ASCII = /[^\t\n\r\x20-\x7E]/gu;
// Invisible offenders (non-breaking or zero-width spaces) are named by code point so they can be found.
const INVISIBLE = /\s|\p{Cf}/u;

/** The distinct characters outside the allowed set, in order of first appearance. */
export const nonAsciiChars = (value: string): string[] => [...new Set(value.match(NON_ASCII) ?? [])];

const showChars = (chars: string[]): string =>
  chars
    .map((c) => (INVISIBLE.test(c) ? `U+${(c.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, '0')}` : c))
    .join(' ');

export const englishOnlyMessage = (chars: string[]): string =>
  `Use English characters only. Remove: ${showChars(chars)}`;

// Typography that word processors, phones and AI tools put in, with plain equivalents. It is converted
// silently rather than rejected, since it isn't another language and people rarely type it on purpose.
const PLAIN_TYPOGRAPHY: [RegExp, string][] = [
  [/ ?[\u2014\u2015] ?/g, ' - '],
  [/[\u2010-\u2013\u2212]/g, '-'],
  [/[\u2018\u2019\u201A\u201B\u2032]/g, "'"],
  [/[\u201C\u201D\u201E\u201F\u2033\u00AB\u00BB]/g, '"'],
  [/\u2026/g, '...'],
  [/[\u2022\u2023\u2043\u25AA\u25CF\u25E6]/g, '-'],
  [/\u2192/g, '->'],
  [/\u2190/g, '<-'],
  [/\u21D2/g, '=>'],
  [/\u2264/g, '<='],
  [/\u2265/g, '>='],
  [/\u2260/g, '!='],
  [/\u00D7/g, 'x'],
  [/\u2044/g, '/'],
  [/[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, ' '],
  [/[\u2028\u2029]/g, '\n'],
  [/\u200B|\u2060|\uFEFF|\u00AD/g, ''],
];

/** Swaps typographic characters (long dashes, curly quotes, ellipsis, special spaces…) for plain ASCII. */
export const plainTypography = (value: string): string =>
  PLAIN_TYPOGRAPHY.reduce((text, [pattern, plain]) => text.replace(pattern, plain), value);

/**
 * English-only text: typography is converted to plain ASCII first, then anything that is still not
 * printable ASCII, a tab or a line break is rejected.
 */
export const englishText = <T extends z.ZodString>(schema: T) =>
  z.preprocess(
    (value) => (typeof value === 'string' ? plainTypography(value) : value),
    schema.superRefine((value, ctx) => {
      const chars = nonAsciiChars(value);
      if (chars.length > 0) ctx.addIssue({ code: z.ZodIssueCode.custom, message: englishOnlyMessage(chars) });
    }),
  );

export const englishTitleField = englishText(titleField);

const isTimeZone = (value: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

// IANA zone (e.g. "Europe/Kyiv") that exported timestamps are rendered in; the API falls back to UTC.
export const timeZoneField = z.string().max(64).refine(isTimeZone, 'Unknown time zone').optional();

// Optional boolean that also accepts the "true"/"false" strings query params arrive as.
export const coercedBoolField = z
  .union([z.boolean(), z.literal('true'), z.literal('false')])
  .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
  .optional();

// A multiselect filter arrives as repeated query params (?coreValues=a&coreValues=b), which the
// query parser yields as a string for one value and an array for many — normalize both to an array.
export const idMultiField = z
  .preprocess((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]), z.array(idField.trim().min(1)))
  .optional();

// ── Sort direction ─────────────────────────────────────────────────────────
export const SORT_ORDERS = {
  ASC: 'asc',
  DESC: 'desc',
} as const;
export type SortOrder = (typeof SORT_ORDERS)[keyof typeof SORT_ORDERS];
export const SORT_ORDER_VALUES = Object.values(SORT_ORDERS) as [SortOrder, ...SortOrder[]];
export const SortOrderSchema = z.enum(SORT_ORDER_VALUES);

// ── Pagination ─────────────────────────────────────────────────────────────
export const CursorQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type CursorQuery = z.infer<typeof CursorQuerySchema>;

export const CursorPageSchema = z.object({
  nextCursor: z.string().nullable(),
});
export type CursorPage = z.infer<typeof CursorPageSchema>;

// Offset/limit pagination — used by admin tables that need a total count and page jumps.
export const OffsetQuerySchema = z.object({
  offset: z.coerce.number().int().min(0).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type OffsetQuery = z.infer<typeof OffsetQuerySchema>;

// ── Base entity ────────────────────────────────────────────────────────────
export const BaseEntitySchema = z.object({
  id: idField,
  createdAt: createdAtField,
});
export type BaseEntity = z.infer<typeof BaseEntitySchema>;

// ── Error envelope ─────────────────────────────────────────────────────────
export const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    details: z.unknown().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;

export const ERROR_CODES = {
  BAD_INPUT: 'bad_input',
} as const;

// `details` of a `bad_input` error: first message per dotted field path (`_` for the whole body).
export const ValidationErrorDetailsSchema = z.object({
  fields: z.record(z.string()),
});
export type ValidationErrorDetails = z.infer<typeof ValidationErrorDetailsSchema>;

// ── Route param validation ─────────────────────────────────────────────────
// Every entity's primary key is a uuid. Anything else (e.g. an idea number) is a 400 here rather
// than reaching Postgres and failing the uuid cast with a 500.
export const idParamSchema = z.string().uuid('Must be a valid id');
